import React, { useEffect, useState, useContext } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Table, Button, notification } from "antd"; // Import notification
import { DatePicker } from "antd";
import {
  getEmployeeAttendance,
  getEmployeeAttendanceCounts,
  manualAttendance,
  getTodayAttendanceStatus,
} from "../../service/employeeAllocationApiService";
import SearchInput from "../components/SearchBar/SearchInput";
import { UserContext } from "../../context/userContext";
import { hasPermission } from "../../core/json/permissions";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Calendar, CalendarClock, CalendarDays, CalendarOff, MapPin, Clock } from "lucide-react";
import { format, startOfDay, endOfDay, setDate, subMonths, isAfter } from "date-fns";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const getSriLankaDate = () => {
  const now = new Date();
  const sriLankaTimeString = now.toLocaleString("en-CA", {
    timeZone: "Asia/Colombo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const [year, month, day] = sriLankaTimeString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const AttendanceEmployee = () => {
  const { user } = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attendanceCounts, setAttendanceCounts] = useState({
    totalWorkingDays: 0,
    absentDays: 0,
    leaveDays: 0,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [attendanceStatus, setAttendanceStatus] = useState({
    nextAction: "Clock In",
    canClockOut: false,
    lastAction: null,
    todayRecords: 0,
  });
  const [isProcessingAttendance, setIsProcessingAttendance] = useState(false);

  // Initialize Ant Design notification
  const [api, contextHolder] = notification.useNotification();

  // Notification helper function
  const showNotification = (type, message, description) => {
    api[type]({
      message,
      description,
      placement: "topRight",
      duration: 4.5,
    });
  };

  const todaySriLanka = getSriLankaDate();
  const defaultStartDate = isAfter(todaySriLanka, setDate(todaySriLanka, 19))
    ? startOfDay(setDate(todaySriLanka, 19))
    : startOfDay(setDate(subMonths(todaySriLanka, 1), 19));
  const defaultEndDate = endOfDay(todaySriLanka);
  const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);

  const columns = [
    {
      title: "Date",
      dataIndex: "attendanceDate",
      key: "attendanceDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Day",
      dataIndex: "dayOfWeek",
      key: "dayOfWeek",
    },
    {
      title: "Clock In",
      dataIndex: "earliestClockIn",
      key: "earliestClockIn",
    },
    {
      title: "Clock Out",
      dataIndex: "latestClockOut",
      key: "latestClockOut",
    },
    {
      title: "Total Hours",
      dataIndex: "totalHours",
      key: "totalHours",
    },
  ];

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  };

  const handleManualAttendance = async () => {
    try {
      setIsProcessingAttendance(true);

      const location = await getCurrentLocation();

      const storedUserData = localStorage.getItem("user");
      if (!storedUserData) {
        throw new Error("User data not found");
      }

      const userData = JSON.parse(storedUserData);
      const userId = userData.data._id;

      const response = await manualAttendance({
        userId,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
      });

      if (response.status === "success") {
        showNotification("success", "Success!", response.message);

        await fetchTodayAttendanceStatus();
        await fetchEmployeeAttendance();
        await fetchAttendanceCounts();
      }
    } catch (error) {
      console.error("Error in manual attendance:", error);

      let errorMessage = "Failed to record attendance";

      if (error.message.includes("Geolocation")) {
        errorMessage = "Please enable location access to record attendance";
      } else if (error.code === 1) {
        errorMessage = "Location access denied. Please enable location permissions.";
      } else if (error.code === 2) {
        errorMessage = "Location unavailable. Please try again.";
      } else if (error.code === 3) {
        errorMessage = "Location request timed out. Please try again.";
      }

      showNotification("error", "Error", errorMessage);
    } finally {
      setIsProcessingAttendance(false);
    }
  };

  const fetchTodayAttendanceStatus = async () => {
    try {
      const storedUserData = localStorage.getItem("user");
      if (!storedUserData) return;

      const userData = JSON.parse(storedUserData);
      const userId = userData.data._id;

      const response = await getTodayAttendanceStatus(userId);

      if (response.status === "success") {
        setAttendanceStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching today attendance status:", error);
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchEmployeeAttendance();
  };

  useEffect(() => {
    fetchEmployeeAttendance();
    fetchAttendanceCounts();
    fetchTodayAttendanceStatus();
  }, [pagination.current, pagination.pageSize, dateRange]);

  const fetchEmployeeAttendance = async () => {
    try {
      const storedUserData = localStorage.getItem("user");
      if (!storedUserData) {
        console.error("User data not found in local storage.");
        showNotification("error", "Error", "User data not found.");
        setLoading(false);
        return;
      }
      const user = JSON.parse(storedUserData);
      const userId = user.data._id;

      const params = {
        userId,
        page: pagination.current,
        limit: pagination.pageSize,
      };

      if (dateRange[0] && dateRange[1]) {
        params.startDate = format(dateRange[0], "yyyy-MM-dd");
        params.endDate = format(dateRange[1], "yyyy-MM-dd");
      } else {
        params.startDate = format(defaultStartDate, "yyyy-MM-dd");
        params.endDate = format(defaultEndDate, "yyyy-MM-dd");
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      console.log("API Request Params:", params);

      const employeeAttendance = await getEmployeeAttendance(params);
      console.log("Employee Attendance Response:", employeeAttendance);

      if (employeeAttendance.data.length === 0) {
        console.log("No attendance records found for the selected filters.");
      }

      setAttendanceData(employeeAttendance.data || []);
      setPagination((prev) => ({
        ...prev,
        total: employeeAttendance.totalResults || 0,
        current: employeeAttendance.page || 1,
        pageSize: employeeAttendance.limit || 5,
      }));
    } catch (error) {
      console.log("Error fetching employee attendance:", error.message);
      showNotification("error", "Error", "Failed to fetch attendance data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceCounts = async () => {
    setLoading(true);
    try {
      const storedUserData = localStorage.getItem("user");
      if (!storedUserData) {
        console.error("User data not found in local storage.");
        showNotification("error", "Error", "User data not found.");
        setLoading(false);
        return;
      }
      const user = JSON.parse(storedUserData);
      const userId = user.data._id;

      const startDate = dateRange[0] || defaultStartDate;
      const endDate = dateRange[1] || defaultEndDate;

      const response = await getEmployeeAttendanceCounts(userId, startDate, endDate);

      console.log("Attendance Counts Response:", response);

      if (response.status === "success" && response.data) {
        setAttendanceCounts({
          totalWorkingDays: response.data.totalWorkingDays || 0,
          absentDays: response.data.absentDays || 0,
          leaveDays: response.data.leaveDays || 0,
        });
      } else {
        console.log("Invalid response from getEmployeeAttendanceCounts:", response);
        setAttendanceCounts({
          totalWorkingDays: 0,
          absentDays: 0,
          leaveDays: 0,
        });
      }
    } catch (error) {
      console.log("Error fetching Attendance counts:", error.message);
      showNotification("error", "Error", "Failed to fetch attendance counts.");
    } finally {
      setLoading(false);
    }
  };

  const dataSource = attendanceData.map((attendance) => ({
    key: attendance.attendanceDate + attendance.earliestClockIn,
    ...attendance,
  }));

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );

  const renderPrinterTooltip = (props) => (
    <Tooltip id="printer-tooltip" {...props}>
      Printer
    </Tooltip>
  );

  const onDateRangeChange = (dates) => {
    setDateRange(dates ? [dates[0].toDate(), dates[1].toDate()] : [null, null]);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <div className="page-wrapper-att">
      {contextHolder} {/* Add notification context holder */}
      <div className="content-att">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-att-container">
              <h1>
                <Calendar size={24} style={{ marginRight: "20px" }} />
                Attendance
              </h1>
              <p>Here, you can efficiently manage all employee details with ease and precision.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="att-management-container">
        <div className="att-management-full">
          {/* Manual Attendance Section */}
          <div
            className="manual-attendance-section"
            style={{
              display: "none",
              marginBottom: "20px",
              padding: "20px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <Clock size={24} color="#1890ff" />
                <div>
                  <h3 style={{ margin: 0, color: "#1890ff" }}>Manual Attendance</h3>
                  <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                    {attendanceStatus.lastAction
                      ? `Last action: ${attendanceStatus.lastAction.type} at ${attendanceStatus.lastAction.time}`
                      : "No attendance recorded today"}
                  </p>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                onClick={handleManualAttendance}
                loading={isProcessingAttendance}
                disabled={isProcessingAttendance}
                style={{
                  backgroundColor: attendanceStatus.nextAction === "Clock In" ? "#52c41a" : "#fa8c16",
                  borderColor: attendanceStatus.nextAction === "Clock In" ? "#52c41a" : "#fa8c16",
                  minWidth: "120px",
                }}
              >
                <MapPin size={16} style={{ marginRight: "5px" }} />
                {attendanceStatus.nextAction}
              </Button>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="icon">
                <Calendar size={20} />
              </div>
              <div className="time-live">
                <span className="hours">{new Date().getDate()}</span>
                <span className="period">
                  {new Date().toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
              <p className="label">Today</p>
            </div>

            <div className="stat-card">
              <div className="icon">
                <CalendarClock size={20} />
              </div>
              <div className="time">
                <span className="hours">{attendanceCounts.totalWorkingDays}</span>
              </div>
              <p className="label">Total Working Days</p>
            </div>

            <div className="stat-card">
              <div className="icon">
                <CalendarDays size={20} />
              </div>
              <div className="time">
                <span className="hours">{attendanceCounts.absentDays}</span>
              </div>
              <p className="label">Absent Days</p>
            </div>

            <div className="stat-card">
              <div className="icon">
                <CalendarOff size={20} />
              </div>
              <div className="time">
                <span className="hours">{attendanceCounts.leaveDays}</span>
              </div>
              <p className="label">Leave Days</p>
            </div>
          </div>

          <div className="page-header">
            <div className="page-title">
              <RangePicker
                format="YYYY-MM-DD"
                onChange={onDateRangeChange}
                defaultValue={[dayjs(defaultStartDate), dayjs(defaultEndDate)]}
                style={{ width: "300px", height: "32px" }}
                allowClear
              />
            </div>
            <ul className="table-top-head">
              <SearchInput searchText={searchQuery} handleSearch={handleSearchInputChange} />
              <li>
                <Button type="primary" onClick={handleSearch} className="att-search-button">
                  Search
                </Button>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <Link>
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderPrinterTooltip}>
                  <Link
                    data-bs-toggle={
                      user?.permissions &&
                      hasPermission(user.permissions, "HRM", "Attendance", "print", "Employee")
                        ? "tooltip"
                        : undefined
                    }
                    data-bs-placement="top"
                    onClick={(e) => {
                      if (
                        !(
                          user?.permissions &&
                          hasPermission(user.permissions, "HRM", "Attendance", "print", "Employee")
                        )
                      ) {
                        e.preventDefault();
                      }
                    }}
                    className={
                      user?.permissions &&
                      hasPermission(user.permissions, "HRM", "Attendance", "print", "Employee")
                        ? ""
                        : "disabled-link"
                    }
                    title={
                      !(
                        user?.permissions &&
                        hasPermission(user.permissions, "HRM", "Attendance", "print", "Employee")
                      )
                        ? "You don't have permission to print employee attendance"
                        : "Print employee attendance"
                    }
                  >
                    <i
                      data-feather="printer"
                      className={`feather-printer ${
                        !(
                          user?.permissions &&
                          hasPermission(user.permissions, "HRM", "Attendance", "print", "Employee")
                        )
                          ? "text-muted"
                        : ""
                      }`}
                    />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="attendance-table-section">
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} items`,
              }}
              rowKey="key"
              className="att-full-width-table"
              locale={{
                emptyText: "No attendance records found for the selected date range",
              }}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceEmployee;